import { defineChatViewActions } from './actionsContext'
import { applyCoreLastMessageToChatOut } from './listHelpers'
import { useChatStore as useChatStoreCore } from '../chatStore'
import { ErrorHandler } from '../../services'
import { handleChatError, validateMessageContent } from '../../utils'
import type { ChatMessage, ReplyTo } from '../../types'
import { debugConsoleError } from '@/modules/chat-matrix-fast/utils/chatDebugLog'

export const sendActions = defineChatViewActions({
    /**
     * 3b.1: timeline больше не копируем в activeChat — ChatLog читает core.messages.
     * Здесь только lastMessage метаданных шапки/превью.
     */
    async updateActiveChatAfterSend() {
      const chatId = this.activeChat?.chat?.id
      if (!chatId || !this.activeChat?.chat) {
        return
      }

      try {
        const chatStore = useChatStoreCore()
        const chatInStore = chatStore.chats.find(c => c.id === chatId)
        if (chatInStore?.last_message) {
          applyCoreLastMessageToChatOut(this.activeChat.chat, chatInStore.last_message)
        } else if (chatStore.messages.length > 0) {
          applyCoreLastMessageToChatOut(
            this.activeChat.chat,
            chatStore.messages[chatStore.messages.length - 1],
          )
        }
        this.activeChat.chat.messages = []
      } catch (error) {
        handleChatError(error, 'Update active chat after send')
      }
    },

    async sendMsg(
      message: ChatMessage['message'],
      replyToId?: number | string,
      replyToMessage?: ReplyTo,
      mentionUserIds?: string[],
    ) {
      const chatStore = useChatStoreCore()

      if (!this.activeChat?.chat) {
        return
      }

      this.initRateLimiters()

      if (!this.messageRateLimiter?.canExecute()) {
        const errorInfo = ErrorHandler.handle(
          new Error('Rate limit exceeded'),
          'Send message',
        )

        this.showNotification(errorInfo.message, 'error')

        return
      }

      try {
        validateMessageContent(message)
        this.messageRateLimiter?.record()
        await chatStore.sendMessage(message, replyToId, replyToMessage, mentionUserIds)
        await this.updateActiveChatAfterSend()
      } catch (error) {
        const errorInfo = ErrorHandler.handle(error, 'Send message')

        this.showNotification(errorInfo.message, 'error')

        throw error
      }
    },

    async sendMessageWithFiles(
      files: File[],
      content?: string,
      replyToId?: number | string,
      replyToMessage?: ReplyTo,
    ) {
      const chatStore = useChatStoreCore()

      if (!this.activeChat?.chat) {
        return
      }

      this.initRateLimiters()

      if (!this.fileMessageRateLimiter?.canExecute()) {
        const errorInfo = ErrorHandler.handle(
          new Error('Rate limit exceeded'),
          'Send message with files',
        )

        this.showNotification(errorInfo.message, 'error')

        return
      }

      try {
        if (content) {
          validateMessageContent(content)
        }

        this.fileMessageRateLimiter?.record()

        await chatStore.sendMessageWithFiles(files, content, replyToId, replyToMessage)

        this.syncWithCoreStore()
        await this.updateActiveChatAfterSend()
      } catch (error) {
        debugConsoleError('[chatViewStore] sendMessageWithFiles: ошибка', error)

        const errorInfo = ErrorHandler.handle(error, 'Send message with files')

        this.showNotification(errorInfo.message, 'error')

        throw error
      }
    },
})
